/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useIntl, defineMessages} from 'react-intl'
import {formatPhoneNumber} from '../../utils/phone-utils'
import {SHIPPING_COUNTRY_CODES, SITE_COUNTRY_MAP} from '../../constants'
import {getPostalCodeRegex, getPostalCodeInputProps} from '../../utils/postal-code-regex'
import {
    stateOptions,
    provinceOptions
} from '@salesforce/retail-react-app/app/components/forms/state-province-options'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useBrand} from '../../contexts/brand-context'

const messages = defineMessages({
    required: {defaultMessage: 'Required', id: 'use_address_fields.error.required'},
    addressTitle: {defaultMessage: 'Address Label (e.g Home, Work)', id: 'use_address_fields.label.address_title'},
    firstName: {defaultMessage: 'First Name', id: 'use_address_fields.label.first_name'},
    lastName: {defaultMessage: 'Last Name', id: 'use_address_fields.label.last_name'},
    phone: {defaultMessage: 'Telephone', id: 'use_address_fields.label.phone'},
    country: {defaultMessage: 'Country', id: 'use_address_fields.label.country'},
    address: {defaultMessage: 'Address', id: 'use_address_fields.label.address'},
    apartment: {defaultMessage: 'Apartment, office, etc.', id: 'use_address_fields.label.apartment'},
    apartmentOptional: {defaultMessage: '(optional)', id: 'use_address_fields.label.apartment_optional'},
    city: {defaultMessage: 'Town', id: 'use_address_fields.label.city'},
    state: {defaultMessage: 'State', id: 'use_address_fields.label.state'},
    province: {defaultMessage: 'Province', id: 'use_address_fields.label.province'},
    zipCode: {defaultMessage: 'Zip Code', id: 'use_address_fields.label.zipCode'},
    postalCode: {defaultMessage: 'Postal Code', id: 'use_address_fields.label.postal_code'},
    stateCodeInvalid: {
        defaultMessage: 'Please enter 2-letter state/province.',
        id: 'use_address_fields.error.state_code_invalid'
    },
    preferred: {defaultMessage: 'Set as my default address', id: 'use_address_fields.label.preferred'},
    invalidPostcode: {defaultMessage: 'Enter a valid postcode', id: 'use_address_fields.error.invalid_postcode'},
    pleaseSelectYourProvince: {defaultMessage: 'Please select your province.', id: 'use_address_fields.error.please_select_your_province'},
    invalidPhoneFormat: {defaultMessage: 'Please enter a valid phone number', id: 'use_address_fields.error.invalid_phone_format'},
    phoneTooltip: {defaultMessage: 'Phone number is required to contact you about your order if needed.', id: 'use_address_fields.tooltip.phone'},
    invalidNameGarage: {defaultMessage: 'Please enter a valid name. Only letters, spaces, hyphens, apostrophes, and periods are allowed.', id: 'use_address_fields.error.invalid_name.garage'},
    invalidNameDynamite: {defaultMessage: 'Please enter a valid name. Only letters, spaces, hyphens, apostrophes, and periods are allowed.', id: 'use_address_fields.error.invalid_name.dynamite'},
    invalidLabelGarage: {defaultMessage: 'Please enter a valid label.', id: 'use_address_fields.error.invalid_label.garage'},
    invalidLabelDynamite: {defaultMessage: 'Please enter a valid label.', id: 'use_address_fields.error.invalid_label.dynamite'}
})

/**
 * A React hook that provides the field definitions for an address form.
 * @param {Object} form - The object returned from `useForm`
 * @param {Object} form.control - The form control object
 * @param {Object} form.formState.errors - An object containing field errors
 * @returns {Object} Field definitions for use in a form
 */

export default function useAddressFields({
    form: {
        watch,
        control,
        formState: {errors}
    },
    prefix = '',
    testIdPrefix = ''
}) {
    const {formatMessage} = useIntl()
    const {site} = useMultiSite()
    const defaultCountry = SITE_COUNTRY_MAP[site?.id] || 'US'

    const countryCode = watch('countryCode') || defaultCountry
    const {isGarage} = useBrand()

    const tid = (name) => testIdPrefix ? {'data-testid': `${testIdPrefix}-input-${name}`} : {}

    const fields = {
        addressTitle: {
            name: `${prefix}addressTitle`,
            label: formatMessage(messages.addressTitle),
            secondaryLabel: formatMessage(messages.apartmentOptional),
            defaultValue: '',
            type: 'text',
            control,
            inputProps: {
                maxLength: 50
            },
            rules: {
                pattern: {
                    value: /^[\p{L}\p{N} .'-]+$/u,
                    message: isGarage
                        ? formatMessage(messages.invalidLabelGarage)
                        : formatMessage(messages.invalidLabelDynamite)
                }
            },
            error: errors[`${prefix}addressTitle`]
        },
        firstName: {
            name: `${prefix}firstName`,
            label: formatMessage(messages.firstName),
            defaultValue: '',
            type: 'text',
            autoComplete: 'given-name',
            rules: {
                required: formatMessage({
                    defaultMessage: 'This field is required',
                    id: 'use_address_fields.error.this_field_is_required'
                }),
                pattern: {
                    value: /^[\p{L}\s.'-]+$/u,
                    message: formatMessage(isGarage ? messages.invalidNameGarage : messages.invalidNameDynamite)
                }
            },
            error: errors[`${prefix}firstName`],
            control,
            inputProps: {
                maxLength: 50,
                ...tid('firstname')
            }
        },
        lastName: {
            name: `${prefix}lastName`,
            label: formatMessage(messages.lastName),
            defaultValue: '',
            type: 'text',
            autoComplete: 'family-name',
            rules: {
                required: formatMessage({
                    defaultMessage: 'This field is required',
                    id: 'use_address_fields.error.this_field_is_required'
                }),
                pattern: {
                    value: /^[\p{L}\s.'-]+$/u,
                    message: formatMessage(isGarage ? messages.invalidNameGarage : messages.invalidNameDynamite)
                }
            },
            error: errors[`${prefix}lastName`],
            control,
            inputProps: {
                maxLength: 50,
                ...tid('lastname')
            }
        },
        phone: {
            name: `${prefix}phone`,
            label: formatMessage(messages.phone),
            defaultValue: '',
            type: 'tel',
            autoComplete: 'tel',
            rules: {
                required: formatMessage(messages.invalidPhoneFormat),
                validate: (value) => {
                    const digits = (value || '').replace(/[^\d]/g, '')
                    if (digits.length !== 10) {
                        return formatMessage(messages.invalidPhoneFormat)
                    }
                    return true
                }
            },
            error: errors[`${prefix}phone`],
            inputProps: ({onChange}) => ({
                inputMode: 'tel',
                maxLength: 14,
                ...tid('phone'),
                onChange(evt) {
                    // Allow only digits, spaces, ( ) - . and optional leading +
                    const filtered = evt.target.value.replace(/[^\d\s()\-.+]/g, '').replace(/(?!^)\+/g, '')
                    onChange(formatPhoneNumber(filtered))
                }
            }),
            control,
            webTooltip: formatMessage(messages.phoneTooltip),
            webTooltipPlacement: 'top',
            ...(testIdPrefix && {webTooltipButtonProps: {'data-testid': `${testIdPrefix}-tooltip-phone`}})
        },
        countryCode: {
            name: `${prefix}countryCode`,
            label: formatMessage(messages.country),
            defaultValue: defaultCountry,
            type: 'select',
            options: SHIPPING_COUNTRY_CODES,
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please select your country.',
                    id: 'use_address_fields.error.please_select_your_country'
                })
            },
            error: errors[`${prefix}countryCode`],
            control,
            // data-private: the selected country (and its option list) is PII for LogRocket
            dataPrivate: true,
            inputProps: {
                ...tid('country')
            }
        },
        address1: {
            name: `${prefix}address1`,
            label: formatMessage(messages.address),
            defaultValue: '',
            type: 'text',
            autoComplete: 'address-line1',
            rules: {
                required: isGarage
                    ? formatMessage({
                          defaultMessage: 'Please enter your address.',
                          id: 'use_address_fields.error.please_select_your_address.garage'
                      })
                    : formatMessage({
                          defaultMessage: 'Please enter your address.',
                          id: 'use_address_fields.error.please_select_your_address.dynamite'
                      })
            },
            error: errors[`${prefix}address1`],
            control,
            inputProps: {
                ...tid('address')
            }
        },
        apartment: {
            name: `${prefix}apartment`,
            label: formatMessage(messages.apartment),
            secondaryLabel: formatMessage(messages.apartmentOptional),
            defaultValue: '',
            type: 'text',
            autoComplete: 'apartment',
            rules: {},
            error: errors[`${prefix}apartment`],
            control,
            inputProps: {
                maxLength: 50,
                ...tid('apartment')
            }
        },
        city: {
            name: `${prefix}city`,
            label: formatMessage(messages.city),
            defaultValue: '',
            type: 'text',
            autoComplete: 'address-level2',
            rules: {
                required: isGarage
                    ? formatMessage({
                          defaultMessage: 'Please enter your city.',
                          id: 'use_address_fields.error.please_select_your_city.garage'
                      })
                    : formatMessage({
                          defaultMessage: 'Please enter your city.',
                          id: 'use_address_fields.error.please_select_your_city.dynamite'
                      })
            },
            error: errors[`${prefix}city`],
            control,
            inputProps: {
                maxLength: 50,
                ...tid('city')
            }
        },
        stateCode: {
            name: `${prefix}stateCode`,
            label: formatMessage(countryCode === 'CA' ? messages.province : messages.state),
            defaultValue: '',
            type: 'select',
            autoComplete: 'address-level1',
            options: (countryCode === 'CA' ? provinceOptions : stateOptions).map(
                ({value, label}) => ({value, label: `${label} (${value})`})
            ),
            rules: {
                required:
                    countryCode === 'CA'
                        ? formatMessage(messages.pleaseSelectYourProvince)
                        : formatMessage({
                              defaultMessage: 'Please select your state.',
                              id: 'use_address_fields.error.please_select_your_state_or_province',
                              description: 'Error message for a blank state (US-specific checkout)'
                          })
            },
            error: errors[`${prefix}stateCode`],
            control,
            // data-private: the selected province/state (and its option list) is PII for LogRocket
            dataPrivate: true,
            ...(testIdPrefix && {inputProps: {...tid('state')}})
        },
        postalCode: {
            name: `${prefix}postalCode`,
            label: formatMessage(countryCode === 'CA' ? messages.postalCode : messages.zipCode),
            defaultValue: '',
            type: 'text',
            autoComplete: 'postal-code',
            rules: {
                required: formatMessage(messages.invalidPostcode),
                validate: (value) => {
                    const regex = getPostalCodeRegex(countryCode)
                    if (regex) {
                        return regex.test(value) || formatMessage(messages.invalidPostcode)
                    }
                    return true
                }
            },
            error: errors[`${prefix}postalCode`],
            control,
            inputProps: {...getPostalCodeInputProps(countryCode), ...tid('postal-code')}
        },
        preferred: {
            name: `${prefix}preferred`,
            label: formatMessage(messages.preferred),
            defaultValue: false,
            type: 'checkbox',
            autoComplete: 'honorific-prefix',
            rules: {},
            control
        }
    }

    return fields
}
